import React from 'react';
import { cn } from '../../shared/utils/cn';
import { Avatar, AvatarImage, AvatarFallback } from '../../shared/components/ui/Avatar/Avatar';
import { useFeaturedCreators } from '../hooks/useFeaturedCreators';

export const SocialProof: React.FC = () => {
  const { creators, loading } = useFeaturedCreators();

  // Fallback placeholder creators if loading or no data
  const fallbackCreators = [
    { name: 'Sarah M.', initial: 'SM', color: 'bg-blue-500' },
    { name: 'Alex K.', initial: 'AK', color: 'bg-green-500' },
    { name: 'Maya L.', initial: 'ML', color: 'bg-purple-500' },
    { name: 'David R.', initial: 'DR', color: 'bg-orange-500' },
    { name: 'Emma W.', initial: 'EW', color: 'bg-pink-500' },
  ];

  // Use real creators if available, otherwise use fallback
  // Always show exactly 5 avatars
  const displayCreators = creators.length > 0 ? creators.slice(0, 5) : fallbackCreators.slice(0, 5);
  const showRealImages = creators.length > 0 && !loading;

  // Get initials from username
  const getInitials = (username: string | null): string => {
    if (!username) return '?';
    const parts = username.split(/[\s._-]/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase().slice(0, 2);
    }
    return username.slice(0, 2).toUpperCase();
  };

  return (
    <div className="w-full py-4 lg:py-8">
      <div className="text-center">
        <p className={cn(
          "font-satoshi text-sm lg:text-base text-gray-600 dark:text-gray-400",
          "mb-3 lg:mb-6"
        )}>
          Trusted by <span className="font-bold text-[var(--color-primary-orange)]">2,000+</span> travel creators and explorers worldwide.
        </p>
        
        {/* Creator avatars */}
        <div className="flex justify-center items-center space-x-2 mb-4">
          {displayCreators.map((creator, index) => {
            const isRealCreator = showRealImages && 'id' in creator;
            const name = isRealCreator ? (creator.username || 'Creator') : (creator as any).name;
            const initial = isRealCreator ? getInitials(creator.username) : (creator as any).initial;
            const color = isRealCreator ? 'bg-gray-400' : (creator as any).color;

            return (
              <div
                key={isRealCreator ? creator.id : index}
                className="relative group"
                style={{ marginLeft: index > 0 ? '-8px' : '0' }}
              >
                {isRealCreator && creator.profile_picture?.url ? (
                  <Avatar className="w-10 h-10 border-2 border-white dark:border-gray-800 shadow-sm hover:scale-110 transition-transform duration-200 cursor-pointer">
                    <AvatarImage src={creator.profile_picture.url} alt={name} />
                    <AvatarFallback className="text-white text-sm font-medium bg-gray-400">
                      {initial}
                    </AvatarFallback>
                  </Avatar>
                ) : (
                  <div className={`w-10 h-10 rounded-full ${color} flex items-center justify-center text-white text-sm font-medium border-2 border-white dark:border-gray-800 shadow-sm hover:scale-110 transition-transform duration-200 cursor-pointer`}>
                    {initial}
                  </div>
                )}
                
                {/* Tooltip */}
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                  {name}
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-l-2 border-r-2 border-t-2 border-transparent border-t-gray-900"></div>
                </div>
              </div>
            );
          })}
          
          {/* Plus more indicator - static number for performance */}
          <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-gray-600 dark:text-gray-400 text-sm font-medium border-2 border-white dark:border-gray-800 shadow-sm">
            +1,995
          </div>
        </div>

        {/* Stats row removed per request */}
      </div>
    </div>
  );
};
